// Core
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';

// Components
import { Ellipsis } from '../../icons';
import { Tooltip, TooltipPlacement } from '../Tooltip';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';

// Component
import {
  MenuItemType,
  Options,
  OptionsDropdownItems,
  OptionsDropdownPlacement,
  OptionsDropdownProps,
  TextWithTooltipType,
} from './OptionsDropdown.types';
import {
  Container,
  Divider,
  Items,
  ListContainer,
  MenuItemsContainer,
  TextContent,
  TextContainer,
  MeasureText,
  FirstItem,
} from './OptionsDropdown.style';

// Simple menu item component (without overflow)
function MenuItem({
  item,
  onClose,
}: MenuItemType): JSX.Element {
  const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
    if (!item.disabled) {
      item.onClick?.(e);
      onClose();
    }
  };

  return (
    <Items
      className="options-dropdown-items"
      color={item.color}
      disabled={item.disabled}
      onClick={handleClick}
    >
      {item.icon}
      <TextContent>
        <Paragraph
          weight={ParagraphWeight.medium}
          size={ParagraphSize.sm}
          preventOverflow
          color="inherit"
        >
          {item.text}
        </Paragraph>
      </TextContent>
    </Items>
  );
}

// Reusable text component with tooltip
function TextWithTooltip({
  text,
  weight = ParagraphWeight.medium,
  size = ParagraphSize.sm,
}: TextWithTooltipType): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

  const checkOverflow = useCallback((): void => {
    const container: HTMLDivElement | null = containerRef.current;
    const measure: HTMLSpanElement | null = measureRef.current;

    if (container && measure) {
      // Measure real text vs available space
      const textWidth: number = measure.scrollWidth;
      const containerWidth: number = container.clientWidth;
      const isOverflowing: boolean = textWidth > containerWidth;

      setIsTooltipVisible(isOverflowing);
    } else {
      setIsTooltipVisible(false);
    }
  }, []);

  useEffect((): (() => void) => {
    const resizeObserver: ResizeObserver = new ResizeObserver(checkOverflow);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Initial check
    const timer: NodeJS.Timeout = setTimeout(checkOverflow, 0);

    return (): void => {
      resizeObserver.disconnect();
      clearTimeout(timer);
    };
  }, [checkOverflow]);

  const textContent = (
    <TextContainer ref={containerRef}>
      <MeasureText ref={measureRef}>
        {text}
      </MeasureText>
      <Paragraph
        weight={weight}
        size={size}
        color="inherit"
        preventOverflow
      >
        {text}
      </Paragraph>
    </TextContainer>
  );

  return isTooltipVisible ? (
    <Tooltip text={text} placement={TooltipPlacement.top}>
      {textContent}
    </Tooltip>
  ) : (
    textContent
  );
}

// Menu item component for first item (without hover colors)
function FirstMenuItem({
  item,
  onClose,
}: MenuItemType): JSX.Element {
  const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
    if (!item.disabled) {
      item.onClick?.(e);
      onClose();
    }
  };

  return (
    <FirstItem
      className="options-dropdown-items"
      color={item.color}
      disabled={item.disabled}
      disabledHover={item.disabledHover}
      onClick={handleClick}
    >
      {item.icon}
      <TextWithTooltip
        text={item.text}
        weight={ParagraphWeight.regular}
        size={ParagraphSize.sm}
      />
    </FirstItem>
  );
}

// Menu item component with tooltip
function MenuItemWithTooltip({
  item,
  onClose,
  weight = ParagraphWeight.medium,
  size = ParagraphSize.sm,
}: MenuItemType): JSX.Element {
  const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
    if (!item.disabled) {
      item.onClick?.(e);
      onClose();
    }
  };

  return (
    <Items
      className="options-dropdown-items"
      color={item.color}
      disabled={item.disabled}
      onClick={handleClick}
    >
      {item.icon}
      <TextWithTooltip text={item.text} weight={weight} size={size} />
    </Items>
  );
}

function OptionsDropdown({
  children = <Ellipsis />,
  className,
  options,
  placement = OptionsDropdownPlacement.bottom,
  secondaryOptions,
  testId,
}: OptionsDropdownProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);

  const handleCloseDropdown = (): void => {
    setOpen(false);
  };

  const getMenuItem = (
    item: Options,
    index: number,
    applyOverflow: boolean,
  ): React.ReactNode => {
    if (!applyOverflow) {
      return <MenuItem item={item} onClose={handleCloseDropdown} />;
    }

    if (index === 0) {
      return (
        <FirstMenuItem
          item={item}
          onClose={handleCloseDropdown}
        />
      );
    }

    return <MenuItemWithTooltip item={item} onClose={handleCloseDropdown} />;
  };

  const menuItems = (
    items: Options[],
    applyOverflow: boolean = false,
  ): OptionsDropdownItems[] => items.map((item, index): OptionsDropdownItems => ({
    key: item.text,
    label: getMenuItem(item, index, applyOverflow),
  }));

  const primaryItems: OptionsDropdownItems[] = options.map((item, index): OptionsDropdownItems => ({
    key: item.text,
    label: getMenuItem(item, index, item.disabledHover === true),
  })); // Apply overflow based on each item's disabledHover property (default: enabled)

  const secondaryItems: {
    key: string,
    label: React.ReactNode,
  }[] = menuItems(secondaryOptions || [], false); // Don't apply overflow

  const renderDropdown = (): JSX.Element => {
    const allItems: Array<{ key: string; element: React.ReactNode }> = [];

    // Add primary items
    primaryItems.forEach((item: OptionsDropdownItems) => {
      allItems.push({
        key: `primary-${item.key}`,
        element: (
          <MenuItemsContainer className="dropdown-container">
            {item.label}
          </MenuItemsContainer>
        ),
      });
    });

    // Add divider if there are secondary items
    if (secondaryItems.length > 0) {
      allItems.push({
        key: 'divider',
        element: <Divider />,
      });
    }

    // Add secondary items
    secondaryItems.forEach((item: OptionsDropdownItems) => {
      allItems.push({
        key: `secondary-${item.key}`,
        element: (
          <MenuItemsContainer>
            {item.label}
          </MenuItemsContainer>
        ),
      });
    });

    return (
      <ListContainer>
        {allItems.map(({ key, element }) => (
          <React.Fragment key={key}>
            {element}
          </React.Fragment>
        ))}
      </ListContainer>
    );
  };

  return (
    <div className={className} data-testid={testId}>
      <Container
        placement={placement}
        dropdownRender={renderDropdown}
        onOpenChange={setOpen}
        open={open}
      >
        <div>{children}</div>
      </Container>
    </div>
  );
}

export { OptionsDropdown };
